import React from "react";
import PropTypes from "prop-types";

const MODES = [
  "primary",
  "border",
  "white",
  "black",
  "ghost",
  "secondary",
  "outline",
  "link",
  "gray",
  "underline",
];

const SIZES = ["xsmall", "small", "medium", "large"];

const Button = ({
  mode = "primary",
  size = "small",
  icon,
  iconPosition = "left",
  children,
  className = "",
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
  type = "button",
  ...rest
}) => {
  const isClickable = !disabled && !loading;
  const hasIcon = Boolean(icon);
  const hasText = Boolean(children);
  const isIconOnly = hasIcon && !hasText;

  const handleClick = (e) => {
    isClickable && onClick?.(e);
  };

  const Icon = () => <span className="button__wrapper--icon">{icon}</span>;

  const Text = () => <span className="button__wrapper--text">{children}</span>;

  const Loading = () => <span className="button__wrapper--spinner"></span>;

  const Content = () => {
    const content = {
      loading: (
        <div className="button__wrapper--content">
          <Loading />
          {hasText && <Text />}
        </div>
      ),
      iconLeft: (
        <div className="button__wrapper--content">
          <Icon />
          <Text />
        </div>
      ),
      iconRight: (
        <div className="button__wrapper--content">
          <Text />
          <Icon />
        </div>
      ),
      iconOnly: <Icon />,
      textOnly: <Text />,
    };

    switch (true) {
      case loading:
        return content.loading;
      case hasIcon && hasText && iconPosition === "left":
        return content.iconLeft;
      case hasIcon && hasText && iconPosition === "right":
        return content.iconRight;
      case isIconOnly:
        return content.iconOnly;
      case hasText:
        return content.textOnly;
      default:
        return null;
    }
  };

  const buttonClasses = [
    "button__wrapper",
    `button__wrapper--${mode}`,
    `button__wrapper--${size}`,
    disabled && "button__wrapper--disabled",
    loading && "button__wrapper--loading",
    isIconOnly && "button__wrapper--icon-only",
    fullWidth && "button__wrapper--full-width",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || loading}
      {...rest}
    >
      {Content()}
    </button>
  );
};

Button.propTypes = {
  mode: PropTypes.oneOf(MODES),
  size: PropTypes.oneOf(SIZES),
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
};

Button.defaultProps = {
  mode: "primary",
  size: "small",
  iconPosition: "left",
  disabled: false,
  loading: false,
  fullWidth: false,
  type: "button",
};

export default Button;
